import { supabase } from '../lib/supabase';
import { log } from '../lib/logger';
import { parseJsonArray } from '../lib/jsonColumn';
import { calculateInfluencerCommission, computeCampaignCommission, getCampaignBaseFee } from '../utils/commissionCalculations';
import { parseDateOnly, daysBetween as daysBetweenLocal } from '../utils/dates';
import { hydrateTableBackedInvoices } from './campaignService';
import {
  computeProjectoPLSummary,
  getItemCostRows,
  listStaffAccounts,
  projectoCampaignService,
  projectoInvoiceService,
  projectoPLService,
} from './projectoService';
import { PROJECTO_TO_CAMPAIGN_STATUS, type ProjectoCampaign, type ProjectoInvoice, type ProjectoPLItem } from '../types/projecto';
import type {
  ProcessedEntry,
  FinancialSummary,
  AgentSummary,
  InfluencerSummary,
  PaymentCategory,
  InvoiceLine,
  MohitInvoiceJson,
  InfluencerInvoiceJson,
} from '../types/financialReport';

const STATUS_EN_TO_PT: Record<string, string> = {
  paid: 'Paga',
  'partially paid': 'Parcialmente Paga',
  issued: 'Emitida',
  overdue: 'Vencida',
  'not issued': 'Não Emitida',
  'not requested': 'Não Solicitada',
  requested: 'Solicitada',
  received: 'Recebida',
  cancelled: 'Cancelada',
  canceled: 'Cancelada',
  draft: 'Rascunho',
};

function normalizeStatus(status: string | undefined | null): string {
  return (status || '').trim().toLowerCase();
}

export function statusToPortuguese(status: string | undefined | null): string {
  const key = normalizeStatus(status);
  if (!key) return '';
  return STATUS_EN_TO_PT[key] || (status ?? '').trim();
}

function statusToCategory(status: string | undefined | null): PaymentCategory {
  const s = normalizeStatus(status);
  if (!s) return 'none';
  if (s === 'paid' || s === 'paga') return 'paid';
  if (s === 'partially paid' || s === 'parcialmente paga') return 'partial';
  if (s === 'overdue' || s === 'vencida') return 'overdue';
  if (s === 'issued' || s === 'emitida' || s === 'requested' || s === 'solicitada' || s === 'received' || s === 'recebida') return 'issued';
  if (s === 'not issued' || s === 'não emitida' || s === 'not requested' || s === 'não solicitada') return 'notissued';
  if (s === 'cancelled' || s === 'canceled' || s === 'cancelada') return 'cancelled';
  if (s === 'draft' || s === 'rascunho') return 'draft';
  return 'none';
}

// Local start-of-day parsing: a date-only string parsed as UTC would flag an
// invoice due today as overdue in Lisbon summer time
function parseDate(value: string | undefined | null): Date | null {
  return parseDateOnly(value);
}

function daysBetween(a: Date, b: Date): number {
  return daysBetweenLocal(a, b);
}

function deriveCampaignCategory(lines: InvoiceLine[]): PaymentCategory {
  if (lines.length === 0) return 'none';
  const categories = lines.map(l => l.category);
  if (categories.every(c => c === 'paid')) return 'paid';
  if (categories.some(c => c === 'overdue')) return 'overdue';
  if (categories.some(c => c === 'paid' || c === 'partial')) return 'partial';
  if (categories.some(c => c === 'issued')) return 'issued';
  if (categories.every(c => c === 'cancelled')) return 'cancelled';
  if (categories.some(c => c === 'notissued')) return 'notissued';
  if (categories.some(c => c === 'draft')) return 'draft';
  return categories[0] ?? 'none';
}

function buildClientLine(inv: MohitInvoiceJson, today: Date): InvoiceLine {
  const rawStatus = (inv.status || '').trim();
  let category = statusToCategory(rawStatus);
  const amount = category === 'cancelled' ? 0 : Number(inv.amount_received || 0);
  const paymentDate = parseDate(inv.payment_received_date || '');
  const paid = category === 'paid' ? amount : 0;
  const balance = amount - paid;

  const dueDate = parseDate(inv.due_date || '');
  let daysOverdue = 0;
  if (category !== 'paid' && category !== 'cancelled' && dueDate) {
    const diff = daysBetween(dueDate, today);
    if (diff > 0) daysOverdue = diff;
  }
  if (category === 'issued' && daysOverdue > 0) category = 'overdue';

  const issuedDate = parseDate(inv.issued_date || '');
  let daysToPayment: number | null = null;
  if (paymentDate && issuedDate) {
    daysToPayment = Math.max(0, daysBetween(issuedDate, paymentDate));
  }

  return {
    side: 'client',
    status_en: rawStatus,
    status_pt: statusToPortuguese(rawStatus),
    category,
    invoice_number: (inv.invoice_number || '').trim(),
    client_invoice_number: (inv.client_invoice_number || '').trim(),
    purchase_order: (inv.purchase_order || '').trim(),
    issued_date: inv.issued_date || '',
    due_date: inv.due_date || '',
    payment_date: inv.payment_received_date || '',
    amount,
    amount_paid: paid,
    balance,
    days_to_payment: daysToPayment,
    days_overdue: daysOverdue,
  };
}

function buildSupplierLine(inv: InfluencerInvoiceJson, today: Date): InvoiceLine {
  const rawStatus = (inv.status || '').trim();
  let category = statusToCategory(rawStatus);
  const amount = category === 'cancelled' ? 0 : Number(inv.amount || 0);
  const paymentDateRaw = inv.payment_date || inv.payment_received_date || '';
  const receivedDateRaw = inv.received_date || inv.issue_date || '';
  const paymentDate = parseDate(paymentDateRaw);
  const paid = category === 'paid' ? amount : 0;
  const balance = amount - paid;

  const issuedDate = parseDate(receivedDateRaw);
  // Supplier invoices are overdue past their own due date, like client ones;
  // only fall back to "30 days after receipt" when none was recorded
  const supplierDue = parseDate((inv as { due_date?: string }).due_date || '')
    ?? (issuedDate ? new Date(issuedDate.getTime() + 30 * 86_400_000) : null);
  let daysOverdue = 0;
  if (category !== 'paid' && category !== 'cancelled' && supplierDue) {
    const diff = daysBetween(supplierDue, today);
    if (diff > 0) daysOverdue = diff;
  }
  if (category === 'issued' && daysOverdue > 0) category = 'overdue';

  let daysToPayment: number | null = null;
  if (paymentDate && issuedDate) {
    daysToPayment = Math.max(0, daysBetween(issuedDate, paymentDate));
  }

  return {
    side: 'supplier',
    label: (inv.source_name || '').trim() || undefined,
    status_en: rawStatus,
    status_pt: statusToPortuguese(rawStatus),
    category,
    invoice_number: (inv.invoice_number || '').trim(),
    client_invoice_number: '',
    purchase_order: '',
    issued_date: receivedDateRaw,
    due_date: '',
    payment_date: paymentDateRaw,
    amount,
    amount_paid: paid,
    balance,
    days_to_payment: daysToPayment,
    days_overdue: daysOverdue,
  };
}

function joinNonEmpty(values: string[]): string {
  return values.map(v => (v || '').trim()).filter(Boolean).join('; ');
}

function formatDateISO(value: string | undefined): string {
  if (!value) return '';
  const d = parseDate(value);
  return d ? d.toISOString().split('T')[0] : '';
}

export function buildProcessedEntry(campaign: any, influencerRows: any[]): ProcessedEntry {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const clientRaw = parseJsonArray<MohitInvoiceJson>(campaign.mohit_invoices);
  const supplierRaw = parseJsonArray<InfluencerInvoiceJson>(campaign.influencer_invoices);

  const clientLines = clientRaw.map(inv => buildClientLine(inv, today));
  const supplierLines = supplierRaw.map(inv => buildSupplierLine(inv, today));

  const client_billed = clientLines.reduce((s, l) => s + l.amount, 0);
  const client_received = clientLines.reduce((s, l) => s + l.amount_paid, 0);
  const supplier_billed = supplierLines.reduce((s, l) => s + l.amount, 0);
  const supplier_paid = supplierLines.reduce((s, l) => s + l.amount_paid, 0);

  let fee: number;
  let commission_amount: number;

  const isIM = campaign.campaign_mode === 'influencer_marketing';
  if (isIM && influencerRows && influencerRows.length > 0 && influencerRows[0].agreed_fee !== undefined) {
    fee = influencerRows.reduce((s: number, r: any) => s + (Number(r.agreed_fee) || 0), 0);
    commission_amount = influencerRows.reduce((s: number, r: any) => {
      return s + calculateInfluencerCommission(
        Number(r.agreed_fee) || 0,
        Number(r.commission_pct) || 0,
        Number(r.commission_flat_fee) || 0,
        r.commission_type || 'percentage'
      );
    }, 0);
  } else {
    // One rule with the dashboard: sponsorship_amount/agency cut, flat fees,
    // revenue-only services. Tolerate a rate stored as 30 instead of 0.30.
    const rawPct = Number(campaign.commission_pct || 0);
    const normalised = rawPct > 1 ? { ...campaign, commission_pct: rawPct / 100 } : campaign;
    fee = getCampaignBaseFee(normalised);
    commission_amount = computeCampaignCommission(normalised);
  }

  // Sponsorships have no creators on file: who they pay is on the invoices
  const rowNames = (influencerRows || [])
    .map(r => r.influencer_name)
    .filter((v: unknown): v is string => !!v);
  const names = rowNames.length > 0
    ? rowNames
    : [...new Set(supplierLines.map(l => l.label).filter((v): v is string => !!v))];

  const client_payment_category = deriveCampaignCategory(clientLines);
  const influencer_payment_category = deriveCampaignCategory(supplierLines);

  const openClientDues = clientLines
    .filter(l => l.category !== 'paid' && l.category !== 'cancelled' && l.due_date)
    .map(l => l.due_date)
    .sort();
  const openSupplierDues = supplierLines
    .filter(l => l.category !== 'paid' && l.category !== 'cancelled' && l.issued_date)
    .map(l => l.issued_date)
    .sort();

  return {
    id: campaign.id,
    work_code: campaign.work_code || '',
    campaign_ref: campaign.campaign_ref || '',
    campaign_mode: campaign.campaign_mode || 'brand_action',
    year: campaign.year,
    client: campaign.client || '',
    brand: campaign.brand || '',
    campaign_status: campaign.campaign_status || 'Consultation',
    agent: campaign.agent_name || '',
    fee,
    commission_pct: fee > 0 ? (commission_amount / fee) * 100 : 0,
    commission_amount,
    influencers: names.join('; '),
    influencer_count: names.length,
    campaign_budget: Number(campaign.campaign_budget || 0),
    notes: campaign.notes || '',
    created_at: campaign.created_at,
    updated_at: campaign.updated_at,

    client_invoice_numbers: joinNonEmpty(clientLines.map(l => l.invoice_number)),
    client_invoice_status: joinNonEmpty(clientLines.map(l => l.status_pt)),
    client_invoice_payment_dates: joinNonEmpty(clientLines.map(l => formatDateISO(l.payment_date))),
    client_invoice_amount: client_billed,

    influencer_invoice_numbers: joinNonEmpty(supplierLines.map(l => l.invoice_number)),
    influencer_invoice_status: joinNonEmpty(supplierLines.map(l => l.status_pt)),
    influencer_invoice_payment_dates: joinNonEmpty(supplierLines.map(l => formatDateISO(l.payment_date))),
    influencer_invoice_amount: supplier_billed,

    client_payment_category,
    influencer_payment_category,
    net_influencer_cost: fee - commission_amount,
    net_agency_margin: client_billed - supplier_billed,

    client_invoices: clientLines,
    supplier_invoices: supplierLines,

    client_billed,
    client_received,
    client_balance_due: client_billed - client_received,
    supplier_billed,
    supplier_paid,
    supplier_balance_due: supplier_billed - supplier_paid,

    next_client_due_date: openClientDues[0] || null,
    next_supplier_due_date: openSupplierDues[0] || null,
    max_client_days_overdue: Math.max(0, ...clientLines.map(l => l.days_overdue)),
    max_supplier_days_overdue: Math.max(0, ...supplierLines.map(l => l.days_overdue)),
  };
}

/** The Account as campaigns spell them: a profile can say "Ana", campaigns "Ana Silva". */
export function matchAgentName(staffName: string, campaignAgents: string[]): string {
  const name = staffName.trim();
  if (!name) return '';
  const lower = name.toLowerCase();
  return campaignAgents.find(a => a.toLowerCase() === lower)
    ?? campaignAgents.find(a => a.toLowerCase().startsWith(`${lower} `))
    ?? name;
}

/**
 * A project reads like any campaign: its value and margin come from the P&L,
 * the client side from the invoices it charges, the supplier side from the
 * costs on its lines. Payable invoices are only a follow-up of those same
 * costs, so counting them too would pay everything twice.
 */
export function buildProjectoEntry(
  project: ProjectoCampaign,
  items: ProjectoPLItem[],
  invoices: ProjectoInvoice[],
  agent: string,
): ProcessedEntry {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const summary = computeProjectoPLSummary(items, invoices, project);

  const clientLines = invoices
    .filter(i => i.invoice_type === 'Receivable')
    .map(i => {
      const line = buildClientLine({
        status: i.status,
        invoice_number: i.number ?? '',
        issued_date: i.issue_date ?? '',
        due_date: i.due_date ?? '',
        payment_received_date: i.paid_date ?? '',
        amount_received: Number(i.subtotal) || 0,
      }, today);
      if (line.category === 'partial') {
        // Typed with VAT; everything here is without it
        const paid = (Number(i.amount_paid) || 0) / (1 + (Number(i.vat_rate) || 0));
        line.amount_paid = Math.min(paid, line.amount);
        line.balance = line.amount - line.amount_paid;
      }
      return line;
    });

  const supplierLines: InvoiceLine[] = items.flatMap(item =>
    getItemCostRows(item)
      .filter(row => (Number(row.amount) || 0) > 0)
      .map(row => {
        const amount = Number(row.amount) || 0;
        return {
          side: 'supplier' as const,
          label: (row.label || '').trim() || item.name,
          status_en: row.paid ? 'Paid' : 'To pay',
          status_pt: row.paid ? 'Paga' : 'Por pagar',
          category: (row.paid ? 'paid' : 'issued') as PaymentCategory,
          invoice_number: '',
          client_invoice_number: '',
          purchase_order: '',
          issued_date: '',
          due_date: '',
          payment_date: '',
          amount,
          amount_paid: row.paid ? amount : 0,
          balance: row.paid ? 0 : amount,
          days_to_payment: null,
          days_overdue: 0,
        };
      })
  );

  const client_billed = clientLines.reduce((s, l) => s + l.amount, 0);
  const client_received = clientLines.reduce((s, l) => s + l.amount_paid, 0);
  const supplier_billed = supplierLines.reduce((s, l) => s + l.amount, 0);
  const supplier_paid = supplierLines.reduce((s, l) => s + l.amount_paid, 0);
  const openClientDues = clientLines
    .filter(l => l.category !== 'paid' && l.category !== 'cancelled' && l.due_date)
    .map(l => l.due_date)
    .sort();

  return {
    id: project.id,
    work_code: project.work_code || '',
    campaign_ref: project.name || '',
    campaign_mode: 'projecto',
    year: new Date(project.created_at).getFullYear(),
    client: project.client_name || '',
    brand: project.brand || project.name || '',
    campaign_status: PROJECTO_TO_CAMPAIGN_STATUS[project.status] ?? project.status,
    agent,
    fee: summary.revenue,
    commission_pct: summary.revenue > 0 ? (summary.margin / summary.revenue) * 100 : 0,
    commission_amount: summary.margin,
    influencers: '',
    influencer_count: 0,
    campaign_budget: project.budget_enabled ? Number(project.budget_amount) || 0 : 0,
    notes: project.notes || '',
    created_at: project.created_at,
    updated_at: project.updated_at,

    client_invoice_numbers: joinNonEmpty(clientLines.map(l => l.invoice_number)),
    client_invoice_status: joinNonEmpty(clientLines.map(l => l.status_pt)),
    client_invoice_payment_dates: joinNonEmpty(clientLines.map(l => formatDateISO(l.payment_date))),
    client_invoice_amount: client_billed,

    influencer_invoice_numbers: '',
    influencer_invoice_status: joinNonEmpty(supplierLines.map(l => l.status_pt)),
    influencer_invoice_payment_dates: '',
    influencer_invoice_amount: supplier_billed,

    client_payment_category: deriveCampaignCategory(clientLines),
    influencer_payment_category: deriveCampaignCategory(supplierLines),
    net_influencer_cost: summary.costs,
    net_agency_margin: summary.margin,

    client_invoices: clientLines,
    supplier_invoices: supplierLines,

    client_billed,
    client_received,
    client_balance_due: client_billed - client_received,
    supplier_billed,
    supplier_paid,
    supplier_balance_due: supplier_billed - supplier_paid,

    next_client_due_date: openClientDues[0] || null,
    next_supplier_due_date: null,
    max_client_days_overdue: Math.max(0, ...clientLines.map(l => l.days_overdue)),
    max_supplier_days_overdue: 0,
    missing_cost_lines: summary.missingCostLines,
  };
}

class FinancialReportService {
  private async projectoEntries(yearFilter: number | undefined, campaignAgents: string[]): Promise<ProcessedEntry[]> {
    try {
      const [projects, items, invoices, staff] = await Promise.all([
        projectoCampaignService.getAll(),
        projectoPLService.getAll(),
        projectoInvoiceService.getAll(),
        listStaffAccounts().catch(() => []),
      ]);
      const staffNames = new Map(staff.map(s => [s.user_id, s.name]));
      return projects
        // A cancelled project doesn't count as anything
        .filter(p => !!PROJECTO_TO_CAMPAIGN_STATUS[p.status])
        .filter(p => !yearFilter || new Date(p.created_at).getFullYear() === yearFilter)
        .map(p => buildProjectoEntry(
          p,
          items.filter(i => i.projecto_id === p.id),
          invoices.filter(i => i.projecto_id === p.id),
          matchAgentName((p.account_id && staffNames.get(p.account_id)) || '', campaignAgents),
        ));
    } catch (err) {
      log.warn('Projectos not loaded for the financial report:', err);
      return [];
    }
  }

  async getAll(yearFilter?: number): Promise<ProcessedEntry[]> {
    let campaignQuery = supabase
      .from('campaigns')
      .select('id, year, work_code, campaign_ref, campaign_mode, client, brand, campaign_status, agent_name, fee, commission_pct, commission_type, commission_flat_fee, campaign_budget, notes, created_at, updated_at, mohit_invoices, influencer_invoices, campaign_expenses, has_payables, sponsorship_amount, agency_commission_percent')
      .order('work_code', { ascending: false });

    if (yearFilter) {
      campaignQuery = campaignQuery.eq('year', yearFilter);
    }

    const { data: campaigns, error } = await campaignQuery;
    if (error) {
      log.error('Error fetching financial report data:', error);
      return [];
    }

    // Influencer marketing and sponsorships keep their invoices in their own table
    await hydrateTableBackedInvoices(campaigns || []);

    const ids = (campaigns || []).map(c => c.id);
    const influencerRowsRes = ids.length
      ? await supabase
          .from('campaign_influencers')
          .select('campaign_id, influencer_name, agreed_fee, commission_pct, commission_flat_fee, commission_type')
          .in('campaign_id', ids)
      : { data: [] as any[] };
    const influencerRows = influencerRowsRes.data || [];

    const influencersByCampaign = new Map<string, any[]>();
    for (const row of influencerRows) {
      const list = influencersByCampaign.get(row.campaign_id) || [];
      list.push(row);
      influencersByCampaign.set(row.campaign_id, list);
    }

    const entries = (campaigns || []).map(c => buildProcessedEntry(c, influencersByCampaign.get(c.id) || []));
    const agents = [...new Set(entries.map(e => e.agent).filter(Boolean))];
    return [...entries, ...await this.projectoEntries(yearFilter, agents)];
  }

  calculateSummary(entries: ProcessedEntry[]): FinancialSummary {
    const summary: FinancialSummary = {
      total_fees: 0,
      total_commissions: 0,
      total_client_invoiced: 0,
      total_influencer_invoiced: 0,
      client_paid: 0,
      client_issued: 0,
      client_overdue: 0,
      client_draft: 0,
      client_unknown: 0,
      influencer_paid: 0,
      influencer_issued: 0,
      influencer_overdue: 0,
      influencer_unknown: 0,
      client_balance_outstanding: 0,
      supplier_balance_outstanding: 0,
      pipeline_fees: 0,
      pipeline_commissions: 0,
      campaigns_completed: 0,
      campaigns_billing: 0,
      campaigns_in_progress: 0,
      campaigns_consultation: 0,
    };

    for (const entry of entries) {
      summary.total_fees += entry.fee;
      summary.total_commissions += entry.commission_amount;
      summary.total_client_invoiced += entry.client_billed;
      summary.total_influencer_invoiced += entry.supplier_billed;
      summary.client_balance_outstanding += entry.client_balance_due;
      summary.supplier_balance_outstanding += entry.supplier_balance_due;

      for (const line of entry.client_invoices) {
        if (line.category === 'paid') summary.client_paid += line.amount;
        else if (line.category === 'partial') {
          summary.client_paid += line.amount_paid;
          summary.client_issued += line.balance;
        } else if (line.category === 'overdue') summary.client_overdue += line.amount;
        else if (line.category === 'issued') summary.client_issued += line.amount;
        else if (line.category === 'draft') summary.client_draft += line.amount;
        else summary.client_unknown += line.amount;
      }

      for (const line of entry.supplier_invoices) {
        if (line.category === 'paid') summary.influencer_paid += line.amount;
        else if (line.category === 'partial') {
          summary.influencer_paid += line.amount_paid;
          summary.influencer_issued += line.balance;
        } else if (line.category === 'overdue') summary.influencer_overdue += line.amount;
        else if (line.category === 'issued') summary.influencer_issued += line.amount;
        else summary.influencer_unknown += line.amount;
      }

      switch (entry.campaign_status) {
        case 'Completed':
          summary.campaigns_completed++;
          break;
        case 'Billing':
          summary.campaigns_billing++;
          break;
        case 'In Progress':
          summary.campaigns_in_progress++;
          break;
        case 'Consultation':
          summary.campaigns_consultation++;
          summary.pipeline_fees += entry.fee;
          summary.pipeline_commissions += entry.commission_amount;
          break;
      }
    }

    return summary;
  }

  calculateAgentSummaries(entries: ProcessedEntry[]): AgentSummary[] {
    const agentMap = new Map<string, AgentSummary>();
    for (const entry of entries) {
      const name = entry.agent || 'Sem account';
      const existing = agentMap.get(name) || {
        name,
        campaign_count: 0,
        total_fees: 0,
        total_commissions: 0,
      };
      existing.campaign_count++;
      existing.total_fees += entry.fee;
      existing.total_commissions += entry.commission_amount;
      agentMap.set(name, existing);
    }
    return Array.from(agentMap.values()).sort((a, b) => b.total_fees - a.total_fees);
  }

  calculateInfluencerSummaries(entries: ProcessedEntry[]): InfluencerSummary[] {
    const influencerMap = new Map<string, InfluencerSummary>();
    for (const entry of entries) {
      const names = entry.influencers
        ? entry.influencers.split(';').map(n => n.trim()).filter(Boolean)
        : [];
      for (const name of names) {
        const existing = influencerMap.get(name) || {
          name,
          campaign_count: 0,
          total_fees: 0,
          total_client_invoiced: 0,
          total_influencer_invoiced: 0,
        };
        existing.campaign_count++;
        existing.total_fees += entry.fee / Math.max(names.length, 1);
        existing.total_client_invoiced += entry.client_billed / Math.max(names.length, 1);
        existing.total_influencer_invoiced += entry.supplier_billed / Math.max(names.length, 1);
        influencerMap.set(name, existing);
      }
    }
    return Array.from(influencerMap.values()).sort((a, b) => b.total_fees - a.total_fees);
  }

  getReceivables(entries: ProcessedEntry[]): ProcessedEntry[] {
    return entries.filter(e => e.client_billed > 0 || e.client_invoices.length > 0);
  }

  getPayables(entries: ProcessedEntry[]): ProcessedEntry[] {
    return entries.filter(e => e.supplier_billed > 0 || e.supplier_invoices.length > 0);
  }

  getPipeline(entries: ProcessedEntry[]): ProcessedEntry[] {
    return entries.filter(e => e.campaign_status === 'Consultation');
  }

  getBillingAttention(entries: ProcessedEntry[]): ProcessedEntry[] {
    return entries.filter(
      e => e.campaign_status === 'Billing' && e.client_payment_category !== 'paid'
    );
  }
}

export const financialReportService = new FinancialReportService();
